import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}

/**
 * Title on the left, actions on the right — and they stay on the right: a long
 * title wraps onto a second line instead of pushing the buttons elsewhere.
 * The title keeps at least 14rem; when the actions do not fit beside it (a
 * page with many actions, a narrow window, a phone) they move to a row of
 * their own under it, still on the right, and wrap there if they must —
 * rather than squeezing the title to a letter per line.
 */
export const PageHeader = ({ title, description, actions, meta }: PageHeaderProps) => (
  <header className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0 flex-1 basis-56">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="min-w-0 break-words text-[18px] font-semibold tracking-tight text-ink">
          {title}
        </h1>
        {meta}
      </div>
      {description ? (
        <p className="mt-1 max-w-3xl text-[13px] text-ink-muted">{description}</p>
      ) : null}
    </div>
    {actions ? (
      <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
        {actions}
      </div>
    ) : null}
  </header>
);
